/*eslint-disable*/
import React, { useEffect, useState } from 'react';
import { useEditor } from '@craftjs/core';
import styles from './styles/styles.module.css';
import UIControlButton from '../BuilderButtons/UIControlButton';
import CustomConfirmationModal from '../CustomConfirmationModal/CustomConfirmationModal';
import { createReportLayout } from '../../api/report/post';
import { useDispatch, useSelector } from 'react-redux';
import {
  setChecklistLayouts,
  setInventoryLayouts,
  setReportLayouts,
  setTrackerLayouts,
} from '../../Redux/slices/UIBuilderLayouts';
import {
  setChecklistTabItems,
  setInventoryTabItems,
  setReportsTabItems,
  setTrackerTabItems,
} from '../../Redux/slices/tabSlice';

const SNAP_TOLERANCE = 25; // px

function getPreviewFieldIdFromSerialized(serialized: string): string | null {
  try {
    const obj = JSON.parse(serialized) as Record<string, any>;
    for (const [key, node] of Object.entries(obj)) {
      if (
        node?.type?.resolvedName === 'InputField' &&
        (node?.props?.previewLabel === true ||
          node?.props?.previewLabel === 'true' ||
          node?.props?.previewLabel === 1)
      ) {
        return key;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/** Build clusters of numbers within a tolerance and return groups with a representative value */
function buildGroups(values: number[], tol = SNAP_TOLERANCE) {
  const sorted = [...values].sort((a, b) => a - b);
  const groups: { values: number[]; rep: number; min: number; max: number }[] =
    [];
  let cur: number[] = [];

  for (const v of sorted) {
    if (!cur.length || Math.abs(v - cur[cur.length - 1]) <= tol) {
      cur.push(v);
    } else {
      const rep = Math.round(cur.reduce((s, n) => s + n, 0) / cur.length);
      groups.push({
        values: cur.slice(),
        rep,
        min: cur[0],
        max: cur[cur.length - 1],
      });
      cur = [v];
    }
  }
  if (cur.length) {
    const rep = Math.round(cur.reduce((s, n) => s + n, 0) / cur.length);
    groups.push({
      values: cur.slice(),
      rep,
      min: cur[0],
      max: cur[cur.length - 1],
    });
  }
  return groups;
}

/** Given groups, map an arbitrary value to the closest group's rep if within tolerance */
function snapToGroups(
  value: number,
  groups: ReturnType<typeof buildGroups>,
  tol = SNAP_TOLERANCE
) {
  let bestRep = value;
  let bestDist = Infinity;
  for (const g of groups) {
    const d = Math.abs(value - g.rep);
    if (d <= tol && d < bestDist) {
      bestDist = d;
      bestRep = g.rep;
    }
  }
  return bestRep;
}

/** Snap all nodes' left/top values when they are close to each other (columns/rows) */
function snapLayoutPositions(serialized: string, tol = SNAP_TOLERANCE): string {
  let obj: Record<string, any>;
  try {
    obj = JSON.parse(serialized);
  } catch {
    return serialized; // if parsing fails, don't block save
  }

  // Collect all numeric left/top values
  const xs: number[] = [];
  const ys: number[] = [];

  for (const [, node] of Object.entries(obj)) {
    const left = node?.props?.left;
    const top = node?.props?.top;
    if (typeof left === 'number') xs.push(left);
    if (typeof top === 'number') ys.push(top);
  }

  if (!xs.length && !ys.length) return serialized;

  const xGroups = buildGroups(xs, tol);
  const yGroups = buildGroups(ys, tol);

  // Apply snapping
  for (const [, node] of Object.entries(obj)) {
    if (!node?.props) continue;
    if (typeof node.props.left === 'number') {
      node.props.left = snapToGroups(node.props.left, xGroups, tol);
    }
    if (typeof node.props.top === 'number') {
      node.props.top = snapToGroups(node.props.top, yGroups, tol);
    }
  }

  return JSON.stringify(obj);
}

/** Make gaps uniform across each detected row and column (after snapping positions) */
function makeUniformGaps(serialized: string, tol = SNAP_TOLERANCE): string {
  let obj: Record<string, any>;
  try {
    obj = JSON.parse(serialized);
  } catch {
    return serialized;
  }

  type NodeInfo = {
    key: string;
    left: number;
    top: number;
    width: number;
    height: number;
    node: any;
  };

  const nodes: NodeInfo[] = [];
  for (const [key, node] of Object.entries(obj)) {
    const p = (node as any)?.props || {};
    if (typeof p.left === 'number' && typeof p.top === 'number') {
      nodes.push({
        key,
        left: p.left,
        top: p.top,
        width: Number.isFinite(p.width) ? Number(p.width) : 0,
        height: Number.isFinite(p.height) ? Number(p.height) : 0,
        node,
      });
    }
  }

  // Group by rows (same top within tol)
  const rowGroups: NodeInfo[][] = [];
  const rowVisited = new Set<string>();
  for (const n of nodes) {
    if (rowVisited.has(n.key)) continue;
    const group = nodes.filter(
      (m) => !rowVisited.has(m.key) && Math.abs(m.top - n.top) <= tol
    );
    group.forEach((m) => rowVisited.add(m.key));
    if (group.length) rowGroups.push(group);
  }

  // For each row: average gap horizontally and reflow from leftmost anchor
  for (const row of rowGroups) {
    if (row.length < 2) continue;
    row.sort((a, b) => a.left - b.left);

    const gaps: number[] = [];
    for (let i = 1; i < row.length; i++) {
      const prev = row[i - 1];
      const curr = row[i];
      const gap = curr.left - (prev.left + prev.width);
      if (gap >= 0) gaps.push(gap);
    }
    if (!gaps.length) continue;

    const desired = Math.round(gaps.reduce((s, g) => s + g, 0) / gaps.length);

    // Anchor first element's left, reflow the rest
    for (let i = 1; i < row.length; i++) {
      const prev = row[i - 1];
      const curr = row[i];
      const newLeft = prev.left + prev.width + desired;
      curr.node.props.left = newLeft;
      curr.left = newLeft; // keep in sync for subsequent calculations
    }
  }

  // Group by columns (same left within tol)
  const colGroups: NodeInfo[][] = [];
  const colVisited = new Set<string>();
  for (const n of nodes) {
    if (colVisited.has(n.key)) continue;
    const group = nodes.filter(
      (m) => !colVisited.has(m.key) && Math.abs(m.left - n.left) <= tol
    );
    group.forEach((m) => colVisited.add(m.key));
    if (group.length) colGroups.push(group);
  }

  // For each column: average gap vertically and reflow from top anchor
  for (const col of colGroups) {
    if (col.length < 2) continue;
    col.sort((a, b) => a.top - b.top);

    const gaps: number[] = [];
    for (let i = 1; i < col.length; i++) {
      const prev = col[i - 1];
      const curr = col[i];
      const gap = curr.top - (prev.top + prev.height);
      if (gap >= 0) gaps.push(gap);
    }
    if (!gaps.length) continue;

    const desired = Math.round(gaps.reduce((s, g) => s + g, 0) / gaps.length);

    // Anchor first element's top, reflow the rest
    for (let i = 1; i < col.length; i++) {
      const prev = col[i - 1];
      const curr = col[i];
      const newTop = prev.top + prev.height + desired;
      curr.node.props.top = newTop;
      curr.top = newTop;
    }
  }

  return JSON.stringify(obj);
}

export const BuilderToolbox: React.FC<any> = ({ setNew }) => {
  const dispatch = useDispatch();
  const { selected } = useSelector((state: any) => state.formToggle);
  const { outputLayout } = useSelector((state: any) => state.builderLayouts);
  const { sites } = useSelector((state: any) => state.associatedSites);

  const { companyPermissions } = useSelector((state: any) => state.user);

  const { query, actions } = useEditor();
  const [serializedForm, setSerialized] = useState<any>(null);
  const [reportName, setReportName] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [documentType, setDocumentType] = useState('REPORT');
  const [changeoverRequirement, setChangeoverRequirement] = useState(false);
  const [changeoverSelection, setChangeoverSelection] = useState<string | null>(
    null
  );
  const [hour, setHour] = useState<string | null>(null);
  const [minute, setMinute] = useState<string | null>(null);
  const [checkboxSelector, setCheckboxSelector] = useState<string | null>(null);
  const [menuItemName, setMenuItemName] = useState<string>('');

  // NEW: keep null/"" as "no selection"
  const [reportLevel, setReportLevel] = useState<string | null>(null);

  const stageForSubmission = () => {
    // NEW: block when Report Level is not selected
    if (!reportLevel || reportLevel.trim() === '') {
      alert(
        'Please select a Report Level (Company or a specific Site) before saving.'
      );
      return;
    }

    if (!reportName.trim() && !reportDescription.trim()) {
      alert(
        'Please enter at least a report name or a description before saving.'
      );
      return;
    }

    if (checkboxSelector === 'NEW' && !menuItemName) {
      alert('Please enter a name to create a new menu item.');
      return;
    }

    if (checkboxSelector === 'EXISTING' && !documentType) {
      alert('Please select an existing menu item to create this report.');
      return;
    }

    const rootNode = query.node('ROOT').toSerializedNode();
    const hasChildren = rootNode.nodes && rootNode.nodes.length > 0;

    if (!hasChildren) {
      alert('Please add at least one input field before saving the layout.');
      return;
    }

    // Temporarily disable grid for cleaner save
    actions.setProp('ROOT', (props: any) => {
      props.showGrid = false;
    });

    setTimeout(() => {
      let serialized = query.serialize();

      // 1) Snap near-equal positions to shared x/y rails
      serialized = snapLayoutPositions(serialized, SNAP_TOLERANCE);

      // 2) Make gaps uniform within each snapped row & column
      serialized = makeUniformGaps(serialized, SNAP_TOLERANCE);

      // ✅ Allow 0 or 1 preview label; block if > 1
      try {
        const obj = JSON.parse(serialized) as Record<string, any>;
        let previewCount = 0;

        for (const [, node] of Object.entries(obj)) {
          if (
            node?.type?.resolvedName === 'InputField' &&
            (node?.props?.previewLabel === true ||
              node?.props?.previewLabel === 'true' ||
              node?.props?.previewLabel === 1)
          ) {
            previewCount++;
          }
        }

        if (previewCount > 1) {
          alert(
            'Only one Input can be marked as the “Preview Label”. Please uncheck extras so only one remains.'
          );
          // Re-enable grid and abort
          actions.setProp('ROOT', (props: any) => {
            props.showGrid = true;
          });
          return;
        }
      } catch {
        // If parsing fails, restore grid and abort
        actions.setProp('ROOT', (props: any) => {
          props.showGrid = true;
        });
        alert('Could not read the layout. Please try again.');
        return;
      }

      setSerialized(serialized);

      // Re-enable grid after saving
      actions.setProp('ROOT', (props: any) => {
        props.showGrid = true;
      });
    }, 0);
  };

  async function submitForm() {
    setSubmissionLoading(true);

    try {
      // Extract preview field id from the previously staged serialized form
      let previewFieldId = getPreviewFieldIdFromSerialized(serializedForm);

      if (!previewFieldId) {
        previewFieldId = reportName;
      }

      const payload = {
        documentType,
        reportName,
        reportDescription,
        // legacy detection for creation date – keep or replace with your backend logic
        mutableCreationDate:
          typeof serializedForm === 'string' &&
          serializedForm.includes('DatePickerField') &&
          serializedForm.includes(`"isCreationDate":true`),
        serializedLayout: serializedForm,
        outputLayout: outputLayout,
        newLayout: checkboxSelector === 'NEW' ? true : false,
        menuItemName: checkboxSelector === 'NEW' ? menuItemName : null,

        // send the unique preview field node id (or report name if missing)
        previewFieldId,
        reportLevel: reportLevel === 'COMPANY' ? null : reportLevel,
        // If your API expects it, uncomment the next line:
        // reportLevel,
      };

      const resp = await createReportLayout(payload);

      if ((resp as any).success) {
        // hydrate report state after new layout creation
        dispatch(setReportLayouts((resp as any).data.reports));
        dispatch(setReportsTabItems((resp as any).data.reports));
        dispatch(setInventoryLayouts((resp as any).data.inventories));
        dispatch(setInventoryTabItems((resp as any).data.inventories));
        dispatch(setChecklistLayouts((resp as any).data.checklists));
        dispatch(setChecklistTabItems((resp as any).data.checklists));
        dispatch(setTrackerLayouts((resp as any).data.trackers));
        dispatch(setTrackerTabItems((resp as any).data.trackers));

        actions.deserialize({
          ROOT: {
            type: { resolvedName: 'SnapCanvas' },
            isCanvas: true,
            props: { showGrid: true, id: 'snap_canvas' },
            displayName: 'SnapCanvas',
            custom: {},
            hidden: false,
            nodes: [],
            linkedNodes: {},
            parent: null, // required by Craft.js
          },
        });

        // ✅ Clear history (undo/redo)
        actions.history.clear();

        // ✅ Reset form state
        setSerialized(null);
        setReportName('');
        setReportDescription('');
        setDocumentType('REPORT');
        setReportLevel(null); // NEW: clear selection
        setNew(false);
      }
    } catch (err) {
      console.log(err);
      alert('Something went wrong');
    } finally {
      setSubmissionLoading(false);
    }
  }

  // nulls the selections if changeover is not required
  useEffect(() => {
    if (!changeoverRequirement) {
      setChangeoverSelection(null);
      setHour(null);
      setMinute(null);
    }
  }, [changeoverRequirement]);

  return (
    <div className={styles.wrapper} style={{ padding: 10 }}>
      {serializedForm && (
        <CustomConfirmationModal
          loading={submissionLoading}
          headline="Save Report"
          onConfirm={submitForm}
          onCancel={() => setSerialized(null)}
        />
      )}
      <h4>Toolbox</h4>
      <div className={styles.checkboxSelector}>
        <div className={styles.checkboxSelectorItem}>
          <input
            checked={checkboxSelector === 'EXISTING'}
            className={styles.selectorCheckbox}
            onChange={() => setCheckboxSelector('EXISTING')}
            type="checkbox"
          />
          <p className={styles.checkboxSelectorLabel}>Existing Menu</p>
        </div>
        <div className={styles.checkboxSelectorItem}>
          <input
            checked={checkboxSelector === 'NEW'}
            className={styles.selectorCheckbox}
            onChange={() => setCheckboxSelector('NEW')}
            type="checkbox"
          />
          <p className={styles.checkboxSelectorLabel}>New Menu</p>
        </div>
      </div>

      <div className={styles.inputContainer}>
        <p className={styles.label}>Report Level</p>
        <select
          value={reportLevel || ''}
          onChange={(e) => setReportLevel(e.target.value)}
          className={styles.inputElDropdown}
        >
          {/* NEW: first line empty (truly blank) */}
          <option value="">{''}</option>
          {companyPermissions.createLayouts && (
            <option value="COMPANY">Company</option>
          )}
          {sites.map((s: any) => {
            return (
              <option key={s.siteName + s.siteCity} value={s._id}>
                {s.siteName}
              </option>
            );
          })}
        </select>
      </div>

      <div className={styles.inputContainer}>
        <p className={styles.label}>Document Type</p>
        <select
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value)}
          className={styles.inputElDropdown}
          disabled={checkboxSelector !== 'EXISTING'}
        >
          <option value="REPORT">Report</option>
          <option value="INVENTORY">Inventory</option>
          <option value="CHECKLIST">Checklist</option>
          <option value="TRACKER">Custody Tracker</option>
        </select>
      </div>

      <div className={styles.inputContainer}>
        <p className={styles.label}>Menu Item Name</p>
        <input
          disabled={checkboxSelector !== 'NEW'}
          className={styles.inputEl}
          value={menuItemName}
          onChange={(e) => setMenuItemName(e.target.value)}
        />
      </div>

      <div className={styles.inputContainer}>
        <p className={styles.label}>Document Name</p>
        <input
          className={styles.inputEl}
          value={reportName}
          onChange={(e) => setReportName(e.target.value)}
        />
      </div>

      <div className={styles.inputContainer}>
        <p className={styles.label}>Document Description</p>
        <input
          className={styles.inputEl}
          value={reportDescription}
          onChange={(e) => setReportDescription(e.target.value)}
        />
      </div>

      {changeoverRequirement && (
        <select
          className={styles.dropdownContainer}
          value={changeoverSelection || ''}
          onChange={(e) => setChangeoverSelection(e.target.value)}
        >
          <option value="" disabled hidden>
            -- Select an option --
          </option>
          <option value="START">{`Shift Start`}</option>
          <option value="END">{`Shift End`}</option>
          <option value="ANYTIME">Anytime</option>
          <option value="TIME">Time</option>
        </select>
      )}
      {changeoverSelection === 'TIME' && (
        <div className={styles.shortHoldingContainer}>
          <div className={styles.shortHoldingSubContainer}>
            <p className={styles.label}>Hour</p>
            <select
              value={hour?.toString() || ''}
              className={styles.dropdownContainerShort}
              onChange={(e) => setHour(e.target.value)}
            >
              {Array.from({ length: 24 }).map((_, index) => (
                <option key={`HOUR_${index}`} value={`${index}`}>
                  {index}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.shortHoldingSubContainer}>
            <p className={styles.label}>Minute</p>
            <select
              value={minute?.toString() || ''}
              className={styles.dropdownContainerShort}
              onChange={(e) => setMinute(e.target.value)}
            >
              {Array.from({ length: 60 }).map((_, index) => {
                return (
                  <option
                    key={`MINUTE_${index}`}
                    value={index > 9 ? `${index}` : `0${index}`}
                  >
                    {index > 9 ? `${index}` : `0${index}`}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      )}

      <div className={styles.functionButtons}>
        <UIControlButton enabled={selected === 'FORM'} type="INPUT" />
        <UIControlButton enabled={selected === 'FORM'} type="CHECKBOX" />
        <UIControlButton enabled={selected === 'FORM'} type="TEXTBOX" />
        {/* <UIControlButton enabled={selected === 'FORM'} type="UPLOAD" /> */}
        <UIControlButton enabled={selected === 'FORM'} type="DROPDOWN" />
        <UIControlButton enabled={selected === 'FORM'} type="DATE" />
        <UIControlButton enabled={selected === 'FORM'} type="TIME" />
        <UIControlButton
          enabled={true}
          type="SAVE"
          loading={submissionLoading}
          submitHandler={stageForSubmission}
        />
      </div>
    </div>
  );
};
